import { useState, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ColumnDef } from '@tanstack/react-table';
import { useDebounceValue } from 'usehooks-ts';

import { FeatureClient, FeatureFlagDtoIn } from '@/models/in/FeatureFlagDtoIn';
import { SystemMessageDtoIn } from '@/models/in/SystemMessageDtoIn';
import DataTable from '@/components/ui/DataTable/DataTable';
import { Col } from '@/components/ui/DataTable/ColumnsHelpers';
import { useGetAuthApi } from '@/hooks/UseApiCalls';
import Button from '@/components/ui/Button';
import StatusFilter from '@/components/filters/StatusFilter/StatusFilter';
import filterStyles from '@/components/filters/FiltersArea.module.scss';
import StateChip from '@/components/ui/StateChip/StateChip';
import useTableDensity from '@/hooks/useTableDensity';
import { FeatureLifecycleStatus } from '@/models/common/FeatureLifecycleStatus';
import { SYSTEM_MESSAGES_MANAGEMENT_URL } from '@/pages/ManagementArea/SystemMessages/systemMessages';
import { relativeTime } from '@/utils/relativeTime';
import HoverHint from '@/components/ui/Tooltip/HoverHint';
import LifecycleStatusChip from './LifecycleStatusChip';
import TargetingNamesHint from './TargetingNamesHint';
import useNameMaps from './useNameMaps';
import EditFeatureFlagDialog from './EditFeatureFlagDialog';
import { matchesFeatureFlagSearch } from './featureFlagsSearch';
import styles from './FeatureFlagsManagementPage.module.scss';

const QUERY_URL = 'management/feature-flags';
const DENSITY_STORAGE_KEY = 'table.density.featureFlags';
const SEARCH_DEBOUNCE_MS = 300;

/*
 * The chips over the list (HAT-10195): what still needs a decision, what is open to everyone, and
 * what was cancelled. A feature open to all has nothing left to decide, so it leaves «בשחרור» by
 * itself - nobody has to remember to finish it for the list to stay short.
 */
type FlagsView = 'releasing' | 'released' | 'retired';
const RELEASING_STATUSES: FeatureLifecycleStatus[] = [FeatureLifecycleStatus.InRollout, FeatureLifecycleStatus.NotReleased];
const CLIENTS: FeatureClient[] = ['Web', 'Nedarim'];
const DOT: Record<FeatureLifecycleStatus, string> = {
  [FeatureLifecycleStatus.InRollout]: 'var(--status-pending-border)',
  [FeatureLifecycleStatus.NotReleased]: 'var(--border-low)',
  [FeatureLifecycleStatus.FullyReleased]: 'var(--status-approved-border)',
};

/* The active work first: a partial rollout is the one being watched, a closed flag is next, open-to-all is done. */
const RANK: Record<FeatureLifecycleStatus, number> = {
  [FeatureLifecycleStatus.InRollout]: 0,
  [FeatureLifecycleStatus.NotReleased]: 1,
  [FeatureLifecycleStatus.FullyReleased]: 2,
};

interface UserRow {
  email: string;
  firstName?: string;
  lastName?: string;
}

const isOpen = (row: FeatureFlagDtoIn) => !row.retiredAt && (!!row.finalizedAt || row.lifecycleStatus !== FeatureLifecycleStatus.NotReleased);
const statusOf = (row: FeatureFlagDtoIn) => (row.finalizedAt ? FeatureLifecycleStatus.FullyReleased : row.lifecycleStatus);
const viewOf = (row: FeatureFlagDtoIn): FlagsView => {
  if (row.retiredAt) return 'retired';
  return statusOf(row) === FeatureLifecycleStatus.FullyReleased ? 'released' : 'releasing';
};

/**
 * «שחרור פיצ'רים» (HAT-1489 rework): a list that says what each feature is, how far it is open,
 * and whether its users have been told - without opening anything. One button per row; history,
 * ending the release and cancelling live inside the flag's dialog.
 *
 * One list per front (HAT-10195): the site and Nedarim are separate fronts, and a feature exists in
 * Nedarim only once it is built there - then it has a row of its own, under the Nedarim tab.
 */
const FeatureFlagsManagementPage = () => {
  const { t, i18n } = useTranslation();

  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch] = useDebounceValue(searchQuery, SEARCH_DEBOUNCE_MS);
  const [density, setDensity] = useTableDensity(DENSITY_STORAGE_KEY, 'normal');
  /*
   * ?flag=<key> opens that flag's dialog on arrival: a feature's announcement links here, to the
   * release that decides when it goes out and to whom. Closing the dialog drops the parameter, so a
   * reload does not open it again.
   */
  const [searchParams, setSearchParams] = useSearchParams();
  const [editKey, setEditKey] = useState<string | null>(() => searchParams.get('flag'));
  const [client, setClient] = useState<FeatureClient>('Web');
  const [view, setView] = useState<FlagsView>('releasing');
  // Inside «בשחרור» only: one status at a time, or both.
  const [statusFilter, setStatusFilter] = useState<FeatureLifecycleStatus | null>(null);

  const { data: flags, isLoading, refetch } = useGetAuthApi<FeatureFlagDtoIn[]>(QUERY_URL);
  // Whether each feature has an announcement, and who last touched it, by name rather than e-mail.
  const { data: messages } = useGetAuthApi<SystemMessageDtoIn[]>(SYSTEM_MESSAGES_MANAGEMENT_URL);
  const { data: users } = useGetAuthApi<UserRow[]>('users');
  // Fetched with the page, so the names behind «4 פרויקטים» are ready by the first hover.
  useNameMaps();

  // Per feature: how many announcements it has, and how many of them are switched on.
  const messagesByKey = useMemo(() => {
    const map = new Map<string, { all: number; on: number }>();
    (messages ?? []).forEach((message) => {
      if (!message.featureFlagKey) return;
      const counts = map.get(message.featureFlagKey) ?? { all: 0, on: 0 };
      map.set(message.featureFlagKey, { all: counts.all + 1, on: counts.on + (message.isActive ? 1 : 0) });
    });
    return map;
  }, [messages]);
  const nameByEmail = useMemo(
    () => new Map((users ?? []).map((user) => [user.email, [user.firstName, user.lastName].filter(Boolean).join(' ')])),
    [users],
  );

  const clientFlags = useMemo(() => (flags ?? []).filter((row) => (row.client ?? 'Web') === client), [flags, client]);

  /*
   * A second switch inside one feature - the rental fees inside «מכשירי סליקה» - is shown in its
   * parent's row rather than as a row of its own. Only while both sit in the same part of the list:
   * a fee switch still closed under a feature open to all must not hide inside a row in another tab.
   */
  const childrenByParent = useMemo(() => {
    const byKey = new Map(clientFlags.map((row) => [row.featureKey, row]));
    const map = new Map<string, FeatureFlagDtoIn[]>();
    clientFlags.forEach((row) => {
      const parent = row.parentFeatureKey ? byKey.get(row.parentFeatureKey) : undefined;
      if (parent && viewOf(parent) === viewOf(row)) map.set(parent.featureKey, [...(map.get(parent.featureKey) ?? []), row]);
    });
    return map;
  }, [clientFlags]);
  const folded = useMemo(() => new Set([...childrenByParent.values()].flat().map((row) => row.featureKey)), [childrenByParent]);
  const listed = useMemo(() => clientFlags.filter((row) => !folded.has(row.featureKey)), [clientFlags, folded]);

  const counts = useMemo(() => {
    const byStatus: Record<FeatureLifecycleStatus, number> = {
      [FeatureLifecycleStatus.NotReleased]: 0,
      [FeatureLifecycleStatus.InRollout]: 0,
      [FeatureLifecycleStatus.FullyReleased]: 0,
    };
    const byView: Record<FlagsView, number> = { releasing: 0, released: 0, retired: 0 };
    listed.forEach((row) => {
      byView[viewOf(row)] += 1;
      if (viewOf(row) === 'releasing') byStatus[row.lifecycleStatus] += 1;
    });
    return { byView, byStatus };
  }, [listed]);

  // What each tab still has to decide on, so the Nedarim tab says at a glance whether it needs a look.
  const pendingByClient = useMemo(() => {
    const map: Record<FeatureClient, number> = { Web: 0, Nedarim: 0 };
    (flags ?? []).forEach((row) => { if (viewOf(row) === 'releasing') map[row.client ?? 'Web'] += 1; });
    return map;
  }, [flags]);

  const shownFlags = useMemo(
    () => listed
      .filter((row) => viewOf(row) === view && (view !== 'releasing' || !statusFilter || row.lifecycleStatus === statusFilter))
      .filter((row) => matchesFeatureFlagSearch(row, debouncedSearch)
        || (childrenByParent.get(row.featureKey) ?? []).some((child) => matchesFeatureFlagSearch(child, debouncedSearch)))
      .sort((a, b) => RANK[a.lifecycleStatus] - RANK[b.lifecycleStatus] || a.displayName.localeCompare(b.displayName, i18n.language === 'en' ? 'en' : 'he')),
    [listed, view, statusFilter, childrenByParent, debouncedSearch, i18n.language],
  );

  const pickView = (next: FlagsView, status: FeatureLifecycleStatus | null = null) => {
    setView(next);
    setStatusFilter(status);
  };

  const openEdit = useCallback((featureKey: string) => {
    if (isLoading) return;
    setEditKey(featureKey);
  }, [isLoading]);

  // ?draft=1: back from editing the announcement - the dialog puts the release's unsaved choices back.
  const restoreDraft = searchParams.get('draft') === '1';
  const handleCloseEdit = useCallback(() => {
    setEditKey(null);
    if (searchParams.has('flag') || searchParams.has('draft')) {
      const next = new URLSearchParams(searchParams);
      next.delete('flag');
      next.delete('draft');
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const locale = i18n.language === 'en' ? 'en-GB' : 'he-IL';

  /** «4 פרויקטים», «2 תפקידים»... one tag each, in the order the admin thinks of them. */
  const rolloutParts = (row: FeatureFlagDtoIn): string[] => {
    const parts: string[] = [];
    if (row.fundsCount) parts.push(t('featureFlags.summary.funds', { count: row.fundsCount }));
    if (row.groupsCount) parts.push(t('featureFlags.summary.groups', { count: row.groupsCount }));
    if (row.classificationsCount) parts.push(t('featureFlags.summary.classifications', { count: row.classificationsCount }));
    if (row.usersCount) parts.push(t('featureFlags.summary.users', { count: row.usersCount }));
    if (row.rolesCount) parts.push(t('featureFlags.summary.roles', { count: row.rolesCount }));
    return parts;
  };

  const columns: ColumnDef<FeatureFlagDtoIn>[] = [
    {
      id: 'displayName',
      header: Col(t('featureFlags.columns.name')),
      accessorKey: 'displayName',
      enableSorting: true,
      // The name over its description, so the row says what the feature is; the key is for the
      // search and the tooltip, not for a column of its own.
      cell: ({ row }) => (
        <HoverHint
          as="div"
          className={styles.feature}
          hint={
            <>
              <strong>{row.original.displayName}</strong>
              {row.original.description && <div>{row.original.description}</div>}
              <div className="muted" dir="ltr">{row.original.featureKey}</div>
            </>
          }
        >
          <span className={styles.featureName}>{row.original.displayName}</span>
          <span className={styles.featureSub}>{row.original.description || <span dir="ltr">{row.original.featureKey}</span>}</span>
        </HoverHint>
      ),
      meta: { className: 'min-w-[300px]' },
    },
    {
      id: 'status',
      header: Col(t('featureFlags.columns.status')),
      accessorKey: 'lifecycleStatus',
      enableSorting: true,
      /*
       * One chip, and under it what the row's date is about: finished, cancelled - and, for a
       * feature with a second switch inside it, that switch, which opens its own release.
       */
      cell: ({ row }) => {
        const { finalizedAt, retiredAt } = row.original;
        return (
          <div className={styles.stack}>
            <LifecycleStatusChip status={statusOf(row.original)} />
            {retiredAt ? (
              <span className={styles.sub}>{t('featureFlags.retiredOn', { date: new Date(retiredAt).toLocaleDateString(locale) })}</span>
            ) : finalizedAt && (
              <span className={styles.sub}>{t('featureFlags.finalizedOn', { date: new Date(finalizedAt).toLocaleDateString(locale) })}</span>
            )}
            {(childrenByParent.get(row.original.featureKey) ?? []).map((child) => (
              <button
                key={child.featureKey}
                type="button"
                className={styles.childLink}
                onClick={(e) => { e.stopPropagation(); openEdit(child.featureKey); }}
              >
                {t('featureFlags.childLine', { name: child.displayName, status: t(`featureFlags.status.${statusOf(child)}`) })}
              </button>
            ))}
          </div>
        );
      },
      meta: { className: 'min-w-[150px]' },
    },
    {
      id: 'targeting',
      header: Col(t('featureFlags.columns.targeting')),
      enableSorting: false,
      cell: ({ row }) => {
        if (row.original.retiredAt) return <span className="text-(--font-body-3)">—</span>;
        const status = statusOf(row.original);
        if (status === FeatureLifecycleStatus.FullyReleased) return <span className="body-sm-regular text-(--font-body-2)">{t('featureFlags.summary.everyone')}</span>;
        if (status === FeatureLifecycleStatus.NotReleased) return <span className="text-(--font-body-3)">—</span>;
        const parts = rolloutParts(row.original);
        if (parts.length === 0) return <span className="body-sm-regular text-(--font-body-3)">{t('featureFlags.summary.none')}</span>;
        return (
          <HoverHint as="div" className={styles.tags} hint={<TargetingNamesHint featureKey={row.original.featureKey} />}>
            <span className={styles.tag}>{parts[0]}</span>
            {parts.length > 1 && <span className={styles.tag}>+{parts.length - 1}</span>}
          </HoverHint>
        );
      },
      meta: { className: 'min-w-[200px]' },
    },
    {
      /*
       * Whether the feature's users have been, or will be, told (HAT-1489). The link between the
       * two screens, visible from the list: a feature that is open with no message is the one to fix.
       */
      id: 'announcement',
      header: Col(t('featureFlags.columns.announcement')),
      enableSorting: false,
      cell: ({ row }) => {
        // Announcements go out in the site's bell; Nedarim has nowhere to show one.
        if (row.original.client === 'Nedarim') return <span className="text-(--font-body-3)">—</span>;
        const counts = messagesByKey.get(row.original.featureKey);
        if (!counts) return <span className="body-sm-regular text-(--font-body-3)">{t('featureFlags.announcement.none')}</span>;
        if (counts.on === 0) return <StateChip tone="grey">{t('featureFlags.announcement.off')}</StateChip>;
        return isOpen(row.original)
          ? <StateChip tone="green">{t('featureFlags.announcement.sent')}</StateChip>
          : <StateChip tone="amber">{t('featureFlags.announcement.onRelease')}</StateChip>;
      },
      meta: { className: 'min-w-[140px]' },
    },
    {
      id: 'updated',
      header: Col(t('featureFlags.columns.updated')),
      accessorKey: 'updatedAt',
      enableSorting: true,
      cell: ({ row }) => {
        const { updatedAt, updatedByEmail } = row.original;
        if (!updatedByEmail && !updatedAt) return <span className="text-(--font-body-3)">—</span>;
        const fullDate = updatedAt ? new Date(updatedAt).toLocaleString(locale) : '';
        return (
          <HoverHint as="div" className={styles.stack} hint={[updatedByEmail, fullDate].filter(Boolean).join(' · ')}>
            <span className="body-sm-regular">{nameByEmail.get(updatedByEmail ?? '') || updatedByEmail?.split('@')[0] || ''}</span>
            <span className={styles.sub}>{relativeTime(updatedAt, t, locale)}</span>
          </HoverHint>
        );
      },
      meta: { className: 'min-w-[150px]' },
    },
    {
      id: 'actions',
      header: Col(''),
      enableSorting: false,
      cell: ({ row }) => (
        <div onClick={(e) => e.stopPropagation()}>
          <Button variant="outline" disabled={isLoading} onClick={() => openEdit(row.original.featureKey)}>
            {t('featureFlags.manage')}
          </Button>
        </div>
      ),
      // Fixed, not min-w: a flexible column is squeezed to fit the screen and its button spills over the edge.
      meta: { className: 'min-w-[110px] w-[110px]', lockLayout: true },
    },
  ];

  return (
    <div className="w-full">
      <DataTable
        title={t('featureFlags.title')}
        data={shownFlags}
        isLoading={isLoading}
        columns={columns}
        filters={
          <div className={filterStyles.filtersArea}>
            <div className={filterStyles.filtersContainer} data-board-filters>
              {/* The front first: two separate lists, never one list with two switches per row. */}
              {CLIENTS.map((c) => (
                <StatusFilter
                  key={c}
                  title={`featureFlags.clients.${c}`}
                  count={pendingByClient[c]}
                  isSelected={client === c}
                  onClick={() => { setClient(c); pickView('releasing'); }}
                />
              ))}
              <span className={styles.divider} aria-hidden="true" />
              {/* No chip of its own for the default view: with none picked, the list is both of these. */}
              {RELEASING_STATUSES.map((status) => (
                <StatusFilter
                  key={status}
                  title={`featureFlags.status.${status}`}
                  count={counts.byStatus[status]}
                  iconColor={DOT[status]}
                  isSelected={view === 'releasing' && statusFilter === status}
                  onClick={() => pickView('releasing', view === 'releasing' && statusFilter === status ? null : status)}
                />
              ))}
              <span className={styles.divider} aria-hidden="true" />
              <StatusFilter
                title="featureFlags.views.released"
                count={counts.byView.released}
                iconColor={DOT[FeatureLifecycleStatus.FullyReleased]}
                isSelected={view === 'released'}
                onClick={() => pickView(view === 'released' ? 'releasing' : 'released')}
              />
              <StatusFilter title="featureFlags.views.retired" count={counts.byView.retired} isSelected={view === 'retired'} onClick={() => pickView(view === 'retired' ? 'releasing' : 'retired')} />
            </div>
          </div>
        }
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        density={density}
        onDensityChange={setDensity}
        handleRowClick={(row) => openEdit(row.featureKey)}
        // The description sits under the name, so the rows take the two-line pitch (as the messages list does).
        twoLineRows
        columnLayoutKey="featureFlags.v2"
        searchPlaceholder="featureFlags.search"
        customGlobalSearch={() => true}
      />

      {editKey && (
        <EditFeatureFlagDialog
          featureKey={editKey}
          open={!!editKey}
          onClose={handleCloseEdit}
          restoreDraft={restoreDraft}
          onSuccess={refetch}
        />
      )}
    </div>
  );
};

export default FeatureFlagsManagementPage;
